import { config } from '@ps/config/client';

import type { PopClipVideoInfo, VideoListing } from './types';
import { VideoType } from './types';

const base = () => config.awsPopclipServiceBaseUrl;

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Fetch a single video clip's info by seller ID and clip ID.
 */
export async function fetchClipInfo(
  sellerId: string,
  clipId: string,
): Promise<PopClipVideoInfo | null> {
  const response = await fetch(`${config.awsMediaGalleryBaseUrl}/mediabyID`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: sellerId, metaIdentifier: clipId }),
  });

  if (!response.ok) return null;

  const data = await response.json();
  if (!data) return null;

  return transformClipItem(data);
}

/**
 * The media gallery's opaque cursor for the NEXT page of a seller's clips
 * (DynamoDB's `lastEvaluatedKey`) — handed back verbatim on the following
 * request, 1:1 with legacy `getClipsBySellerId`. Never built by hand.
 */
export interface SellerClipsCursor {
  id: string;
  metaIdentifier: string;
}

/** One page of a seller's clips; `nextCursor` absent = the pool is exhausted. */
export interface SellerClipsPage {
  clips: PopClipVideoInfo[];
  nextCursor?: SellerClipsCursor;
}

/** `listMediabyID`'s paged envelope. Legacy deployments answer a bare array. */
interface ListMediaByIdResponse {
  items?: Record<string, any>[];
  count?: number;
  lastEvaluatedKey?: SellerClipsCursor;
}

/**
 * Fetch ONE page of the seller-clip library, shaped as `PopClipVideoInfo`s ready
 * for the video-player, plus the cursor for the next page.
 *
 * Hits the same `listMediabyID` endpoint `useCreatorClips` uses, so a player deck
 * receives the exact same set of clips the rail rendered — just hydrated into the
 * player's shape. Cursor paging (rather than a single big top-N) is what lets the
 * All-Videos swipe deck run past one page, matching legacy
 * `getClipsBySellerId` + `usePopClipList`.
 */
export async function fetchClipsPageBySeller(
  sellerId: string,
  {
    pageSize = 50,
    cursor,
    signal,
  }: {
    pageSize?: number;
    cursor?: SellerClipsCursor;
    /** Aborts the request — a caller that pages a whole deck drops the rest. */
    signal?: AbortSignal;
  },
): Promise<SellerClipsPage> {
  const response = await fetch(
    `${config.awsMediaGalleryBaseUrl}/listMediabyID`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileCategory: 'popStoreClips',
        id: sellerId,
        pageSize,
        lastEvaluatedKey: cursor,
      }),
      signal,
    },
  );
  if (!response.ok) return { clips: [] };
  const data: ListMediaByIdResponse | Record<string, any>[] =
    await response.json();
  if (Array.isArray(data)) {
    // Legacy un-paged shape — everything the endpoint has, so no next page.
    return { clips: data.map(item => transformClipItem(item)) };
  }
  return {
    clips: (data?.items ?? []).map(item => transformClipItem(item)),
    nextCursor: data?.lastEvaluatedKey,
  };
}

/**
 * Fetch the FIRST page of the seller-clip library (top-N, no cursor) — the shape
 * every server-side reader wants: the bio All-Videos grid pages by slicing a
 * top-N, and the tab probe only needs existence. Deck players that must swipe
 * past one page use {@link fetchClipsPageBySeller} directly.
 */
export async function fetchClipsBySeller(
  sellerId: string,
  pageSize = 50,
): Promise<PopClipVideoInfo[]> {
  const { clips } = await fetchClipsPageBySeller(sellerId, { pageSize });
  return clips;
}

/**
 * Fetch view count for a single clip.
 */
export async function fetchClipViewCount(clipId: string): Promise<number> {
  try {
    const response = await fetch(`${base()}/api/popclips/views/fetch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: 'psl_popclips',
        clip_ids: [clipId],
      }),
    });
    if (!response.ok) return 0;
    const json = await response.json();
    return json?.data?.[0]?.clip_total_views ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Record a view for a clip (called when video loads/plays).
 */
export async function recordClipView(clipId: string): Promise<void> {
  try {
    await fetch(`${base()}/api/popclips/views`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: 'psl_popclips',
        clip_id: clipId,
        clip_timestamp: Date.now().toString(),
      }),
    });
  } catch {
    // Non-critical
  }
}

/**
 * @deprecated Use fetchAvailableMeetingDates + fetchAvailableMeetingTypes instead
 */
export async function fetchCalendarSlots(
  sellerId: string,
): Promise<{ dates: Date[]; slots: CalendarSlot[] }> {
  try {
    const dates = await fetchAvailableMeetingDates(sellerId);
    return { dates, slots: [] };
  } catch {
    return { dates: [], slots: [] };
  }
}

export type CalendarSlot = {
  id: string;
  startDate: Date;
  durationInSeconds: number;
  price: number;
  sellerId: string;
};

/* ─── Availability API (new) ─── */

export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Fetch available meeting dates for a seller using the Availability API.
 *
 * The endpoint answers a WINDOW starting at `startDate` (7 days), not the whole
 * calendar — pass the day after the last known date to page forward, the same
 * way the creator-side preview (`CalendarAvailability`) does.
 */
export async function fetchAvailableMeetingDates(
  sellerId: string,
  startDate?: Date,
): Promise<Date[]> {
  const tz = getUserTimezone();
  const from = startOfDay(startDate ?? new Date());
  const params = new URLSearchParams({
    startDate: formatDateStr(from),
    timezone: tz,
  });
  const response = await fetch(
    `${config.apiEndpoint}/v1/stores/${encodeURIComponent(sellerId)}/available-meeting-dates?${params.toString()}`,
  );
  if (!response.ok) return [];
  const json = await response.json();
  const items: { date: string }[] = json?.items ?? [];
  return items.map(item => startOfDay(new Date(item.date)));
}

export type MeetingSlotItem = {
  startTime: string;
  endTime: string;
};

export type MeetingType = {
  id: string;
  topic: string;
  durationMinutes: number;
  price: number;
  slots: MeetingSlotItem[];
};

/**
 * Fetch available meeting types for a seller on a given date.
 */
export async function fetchAvailableMeetingTypes(
  sellerId: string,
  date: Date,
): Promise<MeetingType[]> {
  const tz = getUserTimezone();
  const params = new URLSearchParams({
    date: formatDateStr(date),
    timezone: tz,
  });
  const response = await fetch(
    `${config.apiEndpoint}/v1/stores/${encodeURIComponent(sellerId)}/available-meeting-types?${params.toString()}`,
  );
  if (!response.ok) return [];
  const json = await response.json();
  const items: {
    id: string;
    topic: string;
    duration: number;
    price: number;
    slots: MeetingSlotItem[];
  }[] = json?.items ?? [];
  return items.map(item => ({
    id: item.id,
    topic: item.topic,
    durationMinutes: item.duration,
    price: item.price,
    slots: item.slots,
  }));
}

function makeUrl(cdnUrl: string, s: string | undefined): string | undefined {
  if (s == null) return undefined;
  return s.startsWith('https://') ? s : `${cdnUrl}${s}`;
}

/**
 * Fetch like status and count for clips.
 */
export async function fetchClipLikes(
  clipIds: string[],
  userId?: string,
): Promise<{ hasLiked: boolean; likes: number }> {
  try {
    const response = await fetch(`${base()}/api/popclips/likes/fetch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: 'psl_popclips',
        user_id: userId ?? '',
        clip_ids: clipIds,
      }),
    });
    if (!response.ok) return { hasLiked: false, likes: 0 };
    const json = await response.json();
    const data = json?.data?.[0];
    return {
      hasLiked: data?.has_current_user_liked ?? false,
      likes: data?.likes ?? 0,
    };
  } catch {
    return { hasLiked: false, likes: 0 };
  }
}

/**
 * Like a clip.
 */
export async function likeClip(clipId: string, userId: string): Promise<void> {
  await fetch(`${base()}/api/popclips/likes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customer_id: 'psl_popclips',
      clip_id: clipId,
      user_id: userId,
    }),
  });
}

/**
 * Unlike a clip.
 */
export async function unlikeClip(
  clipId: string,
  userId: string,
): Promise<void> {
  const params = new URLSearchParams({ customerId: 'psl_popclips', userId });
  await fetch(`${base()}/api/popclips/${clipId}/likes?${params.toString()}`, {
    method: 'DELETE',
  });
}

/**
 * Transform a series element (from searchSeriesElementsV2) into a PopClipVideoInfo.
 * The V2 popClip shape matches the raw clip item structure.
 */
export function transformSeriesElementToVideo(element: {
  /** Series element row id — needed by remove-from-series.
   *  Distinct from the clip id. */
  id?: string;
  popClip?: {
    metaIdentifier: string;
    sellerId: string;
    cdnUrl: string;
    status?: string;
    metaData: object;
  };
  accessLevel?: string;
}): PopClipVideoInfo | null {
  const popClip = element.popClip;
  if (!popClip) return null;

  const video = transformClipItem({
    metaIdentifier: popClip.metaIdentifier,
    cdnUrl: popClip.cdnUrl,
    metaData: { ...popClip.metaData, sellerId: popClip.sellerId },
  });

  if (element.accessLevel) {
    video.accessLevel = element.accessLevel;
  }

  // Stash the series-element row id on `extra` so the in-player
  // Delete action can route to remove-from-series instead of
  // hard-delete when the clip was opened from a series. The id is
  // distinct from `clip.id` (clipId) — only the series-element row
  // can be deleted via `seriesApiControllerDeleteSeriesElement`.
  if (element.id) {
    video.extra = { ...(video.extra ?? {}), seriesElementId: element.id };
  }

  return video;
}

/**
 * Convert a raw media-gallery clip record into a
 * `PopClipVideoInfo` consumable by the video player. Exported
 * so the All Videos modal (and other surfaces that list clips
 * directly) can build playable arrays from
 * `listClipsBySeller`-style raw payloads without
 * round-tripping `fetchClipInfo` per item.
 */
export function transformClipItem(item: Record<string, any>): PopClipVideoInfo {
  const metaData = item.metaData ?? {};
  const cdnUrl: string = item.cdnUrl ?? '';

  const videoURL =
    metaData.status === 'processed' && metaData.processedURL
      ? makeUrl(cdnUrl, metaData.processedURL)
      : makeUrl(cdnUrl, metaData.videoURL);

  const thumbnailURL = metaData.customThumbnailImageURL
    ? makeUrl(cdnUrl, metaData.customThumbnailImageURL)
    : metaData.defaultThumbnailImageURL &&
        metaData.defaultThumbnailImageStatus === 'processed'
      ? makeUrl(cdnUrl, metaData.defaultThumbnailImageURL)
      : makeUrl(cdnUrl, metaData.thumbnailURL);

  const products: Record<string, any>[] = metaData.products
    ? metaData.products.filter(Boolean)
    : metaData.product
      ? [metaData.product]
      : [];

  const listings: VideoListing[] = products.map((p: Record<string, any>) => ({
    id: p.id ?? '',
    title: p.name,
    defaultImage: p.defaultImage,
    price: p.price,
  }));

  const author = metaData.sellerId
    ? { ...(metaData.profile ?? {}), id: metaData.sellerId }
    : undefined;

  return {
    type: VideoType.Clips,
    id: item.metaIdentifier,
    key: item.metaIdentifier,
    clipId: item.metaIdentifier,
    cdnUrl,
    videoURL: videoURL ?? '',
    videoTitle: metaData.videoTitle,
    videoDescription: metaData.videoDescription,
    thumbnailURL,
    author,
    listings,
    accessLevel: metaData.accessLevel,
    previewDuration: metaData.previewDuration ?? '3',
    showLockedVideoThumbnail: metaData.showLockedVideoThumbnail ?? false,
    muteForUnsubscribedUsers: metaData.muteForUnsubscribedUsers ?? false,
    taggedItemType: metaData.taggedItemType,
    taggedExternalLinks: metaData.taggedExternalLinks,
    metaData: {
      sellerId: metaData.sellerId,
      taggedItemType: metaData.taggedItemType,
      taggedExternalLinks: metaData.taggedExternalLinks,
      transcriptAttachment: metaData.transcriptAttachment,
    },
    createdAt: metaData.createdAt ? new Date(metaData.createdAt) : undefined,
    updatedAt: metaData.updatedAt ? new Date(metaData.updatedAt) : undefined,
  };
}
